import React from 'react';

import { Input } from '@swyftx/aviary/atoms/Input';
import { Select } from '@swyftx/aviary/atoms/Select';
import { Body } from '@swyftx/aviary/atoms/Typography';

import { Controller, useForm } from 'react-hook-form';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationSelector, useLoanApplicationService } from '../../xstate/LoanApplication.context';
import {
  LoanApplicationData,
  EmploymentStatus,
  EmploymentStatusLabels,
  RelationshipStatus,
  RelationshipStatusLabels,
} from '../../xstate/types/LoanApplication.types';

interface TellUsAboutYouFormData {
  employmentStatus: EmploymentStatus;
  relationshipStatus: RelationshipStatus;
  numDependents: number | string;
}

const TellUsAboutYouStep: React.FC = () => {
  const loanApplicationService = useLoanApplicationService();
  const applicationData = useLoanApplicationSelector((state) => state.context.applicationData);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<TellUsAboutYouFormData>({
    mode: 'onChange',
    defaultValues: {
      employmentStatus: (applicationData.employmentStatus as EmploymentStatus) || undefined,
      relationshipStatus: (applicationData.relationshipStatus as RelationshipStatus) || undefined,
      numDependents: applicationData.numDependents ?? '',
    },
  });

  const onSubmit = (data: TellUsAboutYouFormData) => {
    const submissionData = { ...data };
    loanApplicationService.send({ type: 'NEXT', data: submissionData as Partial<LoanApplicationData> });
  };

  return (
    <LoansStepContainer customTitle='Tell us about you' submitDisabled={!isValid} onSubmit={handleSubmit(onSubmit)}>
      <main className='flex flex-col gap-32'>
        <Body>
          As a responsible lender, there are a number of questions we must ask about your financial situation,
          requirements and what you will use the loan for.
        </Body>

        <div className='flex flex-col gap-8'>
          <Body size='small' weight='emphasis'>
            Employment status
          </Body>
          <Controller
            name='employmentStatus'
            control={control}
            rules={{ required: 'Employment status is required' }}
            render={({ field }) => (
              <Select
                {...field}
                placeholder='Select'
                items={Object.entries(EmploymentStatusLabels).map(([value, label]) => ({
                  label,
                  value: value as EmploymentStatus,
                }))}
                onValueChange={field.onChange}
                value={field.value ?? undefined}
              />
            )}
          />
          {errors.employmentStatus && (
            <Body color='error' size='small'>
              {errors.employmentStatus.message}
            </Body>
          )}
        </div>

        <div className='flex flex-col gap-8'>
          <Body size='small' weight='emphasis'>
            Relationship status
          </Body>
          <Controller
            name='relationshipStatus'
            control={control}
            rules={{ required: 'Relationship status is required' }}
            render={({ field }) => (
              <Select
                {...field}
                items={Object.entries(RelationshipStatusLabels).map(([value, label]) => ({
                  label,
                  value: value as RelationshipStatus,
                }))}
                placeholder='Select'
                onValueChange={field.onChange}
                value={field.value ?? undefined}
              />
            )}
          />
          {errors.relationshipStatus && (
            <Body color='error' size='small'>
              {errors.relationshipStatus.message}
            </Body>
          )}
        </div>

        <div className='flex flex-col gap-8'>
          <Body size='small' weight='emphasis'>
            Number of dependents
          </Body>
          <Controller
            name='numDependents'
            control={control}
            rules={{
              required: 'Number of dependents is required',
              min: { value: 0, message: 'Number of dependents cannot be negative' },
              max: { value: 20, message: "🤔 That's a lot of dependents! Please don't include your pets 🐇" },
              validate: (value) =>
                (value !== '' && value !== null && value !== undefined) || 'Number of dependents is required',
            }}
            render={({ field }) => (
              <Input
                {...field}
                type='number'
                title='Number of dependents'
                placeholder='Enter number'
                appearance='outlined'
                onChange={(e) => field.onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                value={
                  field.value === null || field.value === undefined || field.value === '' ? '' : Number(field.value)
                }
              />
            )}
          />
          {errors.numDependents && (
            <Body color='error' size='small'>
              {errors.numDependents.message}
            </Body>
          )}
        </div>
      </main>
    </LoansStepContainer>
  );
};

export { TellUsAboutYouStep };
